import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Download, Filter, X, RefreshCw, SlidersHorizontal, Save, FolderOpen, List, ChevronDown, ChevronUp } from 'lucide-react';
import api, { Watchlist as APIWatchlist } from '../services/api';
import PriceChange from '../components/PriceChange';
import LoadingSpinner from '../components/LoadingSpinner';
import ScreenerAssetRow from '../components/ScreenerAssetRow';
import MobileDataCard from '../components/mobile/MobileDataCard';
import { JSE_STOCKS, US_STOCKS, CRYPTOCURRENCIES, COMMODITIES } from '../utils/constants';
import { getCached, setCache, CACHE_TTL } from '../services/cache';
import { PermissionGate } from '../components/PermissionGate';
import { UpgradeNudge } from '../components/UpgradeNudge';
import { usePermission } from '../hooks/usePermission';
import { useAuth } from '../contexts/AuthContext';
import type { EnrichedSignal } from '../types/signals';

type SignalType = 'strong_buy' | 'buy' | 'neutral' | 'sell' | 'strong_sell';

interface SignalBadgeProps {
  signal: SignalType;
}

const SignalBadge: React.FC<SignalBadgeProps> = ({ signal }) => {
  const styles: Record<SignalType, string> = {
    strong_buy: 'bg-emerald-500/20 text-emerald-400',
    buy: 'bg-emerald-700/20 text-emerald-300',
    neutral: 'bg-slate-500/20 text-slate-400',
    sell: 'bg-red-700/20 text-red-300',
    strong_sell: 'bg-red-500/20 text-red-400',
  };
  const labels: Record<SignalType, string> = {
    strong_buy: '🟢 Strong Buy',
    buy: '🟢 Buy',
    neutral: '⚪ Hold',
    sell: '🔴 Sell',
    strong_sell: '🔴 Strong Sell',
  };
  return <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${styles[signal]}`}>{labels[signal]}</span>;
};

interface ScreenerResultItem {
  symbol: string;
  name: string;
  price: number;
  change: number;
  previousClose?: number;
  rsi: number;
  macd: number;
  sma50: number;
  signal: SignalType;
  timing?: 'now' | 'next_candle' | 'watch';
  exchange: string;
  currency: string;
  marketCap: string;
  sector: string;
  volume: string;
  aiRecommendation?: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell';
  aiConfidence?: number;
  dayDropOverride?: boolean;
  strategyMatch?: boolean;
  matchedStrategy?: string;
  strategyBreakdown?: EnrichedSignal | null;
}

const isSignalVisibilityAllowed = (user: ReturnType<typeof useAuth>['user']) => {
  if (!user?.fais_cat_ii_compliant) return false;
  if (!user.fais_cat_ii_compliance_valid_until) return true;

  const rawExpiry = user.fais_cat_ii_compliance_valid_until;
  const expiry = new Date(rawExpiry);
  if (Number.isNaN(expiry.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return expiry >= today;
};

interface ScreenerFilters {
  markets: string[];
  sectors: string[];
  signalTypes: SignalType[];
  timingFilter: ('now' | 'next_candle' | 'watch')[];
  rsiMin: number;
  rsiMax: number;
  changeMin: number;
  changeMax: number;
  priceAboveSma: boolean | null;
}

const defaultFilters: ScreenerFilters = {
  markets: ['JSE', 'US', 'Crypto', 'Commodities'],
  sectors: [],
  signalTypes: [],
  timingFilter: [],
  rsiMin: 0,
  rsiMax: 100,
  changeMin: -100,
  changeMax: 100,
  priceAboveSma: null,
};

interface SavedFilter {
  name: string;
  filters: ScreenerFilters;
  createdAt: string;
}

const SAVED_FILTERS_KEY = 'screener_saved_filters';

const Screener: React.FC = () => {
  const { user } = useAuth();
  const hasSignalDirection = isSignalVisibilityAllowed(user);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<ScreenerResultItem[]>([]);
  const [filteredResults, setFilteredResults] = useState<ScreenerResultItem[]>([]);
  const [watchlist, setWatchlist] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState<ScreenerFilters>(() => {
    // Load last used filters from localStorage
    const saved = localStorage.getItem('screener_last_filters');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Migrate old filters to include new fields
      return {
        ...defaultFilters,
        ...parsed,
        timingFilter: parsed.timingFilter || [],
      };
    }
    return defaultFilters;
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'change' | 'rsi' | 'price' | 'name'>('change');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>(() => {
    const saved = localStorage.getItem(SAVED_FILTERS_KEY);
    return saved ? JSON.parse(saved) : [];
  });
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');
  const [showLoadMenu, setShowLoadMenu] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20); // Results per page
  const [watchlists, setWatchlists] = useState<APIWatchlist[]>([]);
  const [selectedWatchlistId, setSelectedWatchlistId] = useState<number | null>(null);
  const [aiLoadingSymbols, setAiLoadingSymbols] = useState<Set<string>>(new Set());
  const [strategyLoadingSymbols, setStrategyLoadingSymbols] = useState<Set<string>>(new Set());
  const [myStrategyOnly, setMyStrategyOnly] = useState(false);
  const [strategyMatchDone, setStrategyMatchDone] = useState(false);
  const [strategyMatchLoading, setStrategyMatchLoading] = useState(false);
  const [strategyMatchError, setStrategyMatchError] = useState(false);
  const [strategyMatchTimestamp, setStrategyMatchTimestamp] = useState<Date | null>(null);
  const { allowed: hasStrategyPermission } = usePermission('feature:strategy-driven-signals');

  const availableSectors = ['Technology', 'Financials', 'Mining', 'Retail', 'Healthcare', 'Energy', 'Industrials', 'Telecoms', 'Consumer Goods', 'Consumer Discretionary', 'Media', 'Conglomerate', 'Property', 'Commodities'];
  const signalOptions: { value: SignalType; label: string }[] = [
    { value: 'strong_buy', label: 'Strong Buy' },
    { value: 'buy', label: 'Buy' },
    { value: 'neutral', label: 'Hold' },
    { value: 'sell', label: 'Sell' },
    { value: 'strong_sell', label: 'Strong Sell' },
  ];
  const timingOptions: { value: 'now' | 'next_candle' | 'watch'; label: string; icon: string }[] = [
    { value: 'now', label: 'NOW', icon: '⚡' },
    { value: 'next_candle', label: 'Next Candle', icon: '⏳' },
    { value: 'watch', label: 'Watch', icon: '👁️' },
  ];

  useEffect(() => {
    loadScreenerResults();
    loadWatchlists();
  }, []);

  useEffect(() => {
    applyFilters();
    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, [results, filters, searchQuery, sortBy, sortOrder, selectedWatchlistId, myStrategyOnly]);

  // Auto-save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('screener_last_filters', JSON.stringify(filters));
  }, [filters]);

  const saveCurrentFilter = () => {
    if (!newFilterName.trim()) return;

    const newSavedFilter: SavedFilter = {
      name: newFilterName.trim(),
      filters: { ...filters },
      createdAt: new Date().toISOString(),
    };

    const updated = [...savedFilters, newSavedFilter];
    setSavedFilters(updated);
    localStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(updated));
    setNewFilterName('');
    setShowSaveDialog(false);
  };

  const loadSavedFilter = (savedFilter: SavedFilter) => {
    // Migrate old saved filters to include new fields
    const migratedFilters = {
      ...defaultFilters,
      ...savedFilter.filters,
      timingFilter: savedFilter.filters.timingFilter || [],
    };
    setFilters(migratedFilters);
    setShowLoadMenu(false);
  };

  const deleteSavedFilter = (index: number) => {
    const updated = savedFilters.filter((_, i) => i !== index);
    setSavedFilters(updated);
    localStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(updated));
  };

  const runStrategyMatch = async () => {
    setStrategyMatchLoading(true);
    setStrategyMatchError(false);
    try {
      const activeMarkets = filters.markets.filter(m => m === 'JSE' || m === 'US');
      const markets = activeMarkets.length > 0 ? activeMarkets : ['JSE', 'US'];
      const responses = await Promise.all(
        markets.map(m => api.postStrategyMatch(m.toLowerCase()))
      );
      // Merge strategy fields from all market responses into results
      const strategyBySymbol = new Map<string, { strategyMatch?: boolean; matchedStrategy?: string }>();
      for (const response of responses) {
        for (const signal of response.signals) {
          strategyBySymbol.set(signal.symbol, {
            strategyMatch: signal.strategy_match,
            matchedStrategy: signal.matched_strategy,
          });
        }
      }
      setResults(prev =>
        prev.map(item => {
          const match = strategyBySymbol.get(item.symbol);
          return match ? { ...item, strategyMatch: match.strategyMatch, matchedStrategy: match.matchedStrategy } : item;
        })
      );
      setStrategyMatchDone(true);
      setStrategyMatchTimestamp(new Date());
    } catch (err) {
      console.error('Strategy match failed:', err);
      setStrategyMatchError(true);
    } finally {
      setStrategyMatchLoading(false);
    }
  };

  const loadScreenerResults = async () => {
    try {
      setLoading(true);

      const cacheKey = 'screener_all_results';
      const cached = getCached<ScreenerResultItem[]>(cacheKey);
      if (cached) {
        setResults(cached);
        setLoading(false);
        return;
      }

      // Load JSE first — show results immediately without waiting for US/crypto/commodities.
      const jseResponse = await api.getJSEScreenerSignals();
      const jseSignals = jseResponse.signals || [];

      // Map helper — defined inline so it can be reused below
      const mapSignals = (signals: typeof jseSignals): ScreenerResultItem[] =>
        signals.map((signal) => {
          const isJSE = signal.symbol.endsWith('.JSE');
          const isCrypto = signal.symbol.includes('-USD.CC');
          const commoditySymbols = COMMODITIES.map(c => c.symbol);
          const isCommodity = commoditySymbols.includes(signal.symbol);
          const symbolName = signal.symbol.split('.')[0];
          const assetDetails = isJSE
            ? JSE_STOCKS.find(s => s.symbol === signal.symbol)
            : isCrypto
            ? CRYPTOCURRENCIES.find(c => c.symbol === signal.symbol)
            : isCommodity
            ? COMMODITIES.find(c => c.symbol === signal.symbol)
            : US_STOCKS.find(s => s.symbol === signal.symbol);

          if (signal.symbol === 'XAGUSD.FOREX') {
            console.log('[Screener] XAGUSD Signal from backend:', {
              symbol: signal.symbol,
              price: signal.price,
              change_percent: signal.change_percent,
              previousClose: signal.previousClose,
            });
          }

          return {
            symbol: signal.symbol,
            name: assetDetails?.name || symbolName,
            price: signal.price,
            change: signal.change_percent,
            previousClose: signal.previousClose,
            rsi: signal.rsi || 50,
            macd: 0,
            sma50: signal.price * 0.98,
            signal: signal.signal as SignalType,
            timing: signal.timing,
            exchange: isJSE ? 'JSE' : isCrypto ? 'Crypto' : isCommodity ? 'Commodities' : 'NASDAQ',
            currency: isJSE ? 'R' : '$',
            marketCap: isCrypto ? `${Math.round(Math.random() * 500)}B` : isCommodity ? 'N/A' : `${Math.round(Math.random() * 50)}B`,
            sector: (assetDetails && 'sector' in assetDetails) ? assetDetails.sector : isCommodity ? 'Commodities' : 'Technology',
            volume: 'N/A',
            dayDropOverride: signal.day_drop_override ?? false,
            strategyMatch: signal.strategy_match,
            matchedStrategy: signal.matched_strategy,
            strategyBreakdown: signal.strategy_breakdown && signal.matched_strategy && signal.match_score !== undefined
              ? {
                  symbol: signal.symbol,
                  price: signal.price,
                  generated_at: '',
                  strategies_evaluated: signal.strategies_evaluated ?? 0,
                  strategies_matched: signal.strategies_matched ?? 0,
                  match_score: signal.match_score,
                  matched_strategy: signal.matched_strategy,
                  direction: 'HOLD' as const,
                  confidence: signal.match_score,
                  convergence_count: signal.convergence_count ?? 0,
                  convergence_boost: signal.convergence_boost ?? 0,
                  convergence_sources: signal.convergence_sources ?? [],
                  strategy_breakdown: signal.strategy_breakdown,
                }
              : null,
          };
        });

      // Show JSE results immediately — unblock the UI
      const jseResults = mapSignals(jseSignals);
      setResults(jseResults);
      setLoading(false);

      // Fetch remaining markets in background without blocking the UI
      const [usResponse, cryptoResponse, commoditiesResponse] = await Promise.all([
        api.getUSScreenerSignals(),
        api.getCustomScreenerSignals(CRYPTOCURRENCIES.map(c => c.symbol)),
        api.getCustomScreenerSignals(COMMODITIES.map(c => c.symbol)),
      ]);

      const allSignals = [
        ...jseSignals,
        ...(usResponse.signals || []),
        ...(cryptoResponse.signals || []),
        ...(commoditiesResponse.signals || []),
      ];

      const resultsData: ScreenerResultItem[] = mapSignals(allSignals);

      setCache(cacheKey, resultsData, 5 * 60 * 1000);  // 5-minute localStorage cache
      setResults(resultsData);

      // Fetch AI recommendations for all symbols in background
      loadAIRecommendations(resultsData.map(r => r.symbol));
    } catch (error) {
      console.error('Error loading screener results:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAIRecommendations = async (symbols: string[]) => {
    // Fetch AI recommendations for each symbol (silently fail if not available)
    const aiPromises = symbols.map(async (symbol) => {
      try {
        const aiData = await api.getLatestAIAnalysis(symbol);
        return { symbol, aiData };
      } catch (err) {
        // Silently fail - AI recommendation is optional
        return { symbol, aiData: null };
      }
    });

    const aiResults = await Promise.all(aiPromises);

    // Update results with AI data
    setResults(prevResults =>
      prevResults.map(result => {
        const aiResult = aiResults.find(ai => ai.symbol === result.symbol);
        if (aiResult && aiResult.aiData) {
          return {
            ...result,
            aiRecommendation: aiResult.aiData.recommendation,
            aiConfidence: aiResult.aiData.confidence,
          };
        }
        return result;
      })
    );
  };

  const requestAIAnalysis = async (symbol: string) => {
    try {
      // Add to loading set
      setAiLoadingSymbols(prev => new Set(prev).add(symbol));

      // Find the current result to get price and change data
      const currentResult = results.find(r => r.symbol === symbol);

      // Auto-populate additional_context with current price and change if available
      let additionalContext: string | undefined;
      if (currentResult) {
        const changeSign = currentResult.change >= 0 ? '+' : '';
        additionalContext = `Current price: ${currentResult.currency}${currentResult.price.toFixed(2)}, Change: ${changeSign}${currentResult.change.toFixed(2)}%`;

        // Add previous close if available
        if (currentResult.previousClose) {
          additionalContext += `, Previous close: ${currentResult.currency}${currentResult.previousClose.toFixed(2)}`;
        }
      }

      // Request new AI analysis with auto-populated context
      const aiData = await api.getAIAnalysis(symbol, 'comprehensive', additionalContext);

      // Update the result with new AI data
      setResults(prevResults =>
        prevResults.map(result => {
          if (result.symbol === symbol) {
            return {
              ...result,
              aiRecommendation: aiData.recommendation,
              aiConfidence: aiData.confidence,
            };
          }
          return result;
        })
      );
    } catch (error) {
      console.error(`Failed to get AI analysis for ${symbol}:`, error);
      // Could show a toast notification here
    } finally {
      // Remove from loading set
      setAiLoadingSymbols(prev => {
        const newSet = new Set(prev);
        newSet.delete(symbol);
        return newSet;
      });
    }
  };

  const requestStrategyBreakdown = async (symbol: string) => {
    try {
      setStrategyLoadingSymbols(prev => new Set(prev).add(symbol));
      const breakdown = await api.getStrategyBreakdown(symbol);
      setResults(prevResults =>
        prevResults.map(result =>
          result.symbol === symbol
            ? { ...result, strategyBreakdown: breakdown }
            : result
        )
      );
    } catch (error) {
      console.error(`Failed to get strategy breakdown for ${symbol}:`, error);
    } finally {
      setStrategyLoadingSymbols(prev => {
        const newSet = new Set(prev);
        newSet.delete(symbol);
        return newSet;
      });
    }
  };

  const loadWatchlists = async () => {
    try {
      const lists = await api.getWatchlists();
      setWatchlists(lists);

      // Update local watchlist state with all symbols from all watchlists
      const allSymbols: string[] = [];
      lists.forEach(list => {
        list.items.forEach(item => {
          if (!allSymbols.includes(item.symbol)) {
            allSymbols.push(item.symbol);
          }
        });
      });
      setWatchlist(allSymbols);
    } catch (error) {
      console.error('Error loading watchlists:', error);
      // Silently fail - user might not be logged in or watchlists feature disabled
    }
  };

  const applyFilters = () => {
    let filtered = [...results];

    // Watchlist filter - apply FIRST before other filters
    if (selectedWatchlistId !== null) {
      const selectedWatchlist = watchlists.find(w => w.id === selectedWatchlistId);
      if (selectedWatchlist) {
        const watchlistSymbols = selectedWatchlist.items.map(item => item.symbol);
        filtered = filtered.filter(item => watchlistSymbols.includes(item.symbol));
      }
    }

    // Search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.symbol.toLowerCase().includes(query) ||
          item.name.toLowerCase().includes(query)
      );
    }

    // Market filter
    if (filters.markets.length > 0) {
      filtered = filtered.filter((item) => {
        if (filters.markets.includes('JSE') && item.exchange === 'JSE') return true;
        if (filters.markets.includes('US') && item.exchange === 'NASDAQ') return true;
        if (filters.markets.includes('Crypto') && item.exchange === 'Crypto') return true;
        if (filters.markets.includes('Commodities') && item.exchange === 'Commodities') return true;
        return false;
      });
    }

    // Sector filter
    if (filters.sectors.length > 0) {
      filtered = filtered.filter((item) => filters.sectors.includes(item.sector));
    }

    // Signal type filter (disabled unless CAT II signal access is enabled)
    if (hasSignalDirection && filters.signalTypes.length > 0) {
      filtered = filtered.filter((item) => filters.signalTypes.includes(item.signal));
    }

    // Timing filter
    if (filters.timingFilter.length > 0) {
      filtered = filtered.filter((item) => item.timing && filters.timingFilter.includes(item.timing));
    }

    // RSI filter
    filtered = filtered.filter(
      (item) => item.rsi >= filters.rsiMin && item.rsi <= filters.rsiMax
    );

    // Change filter
    filtered = filtered.filter(
      (item) => item.change >= filters.changeMin && item.change <= filters.changeMax
    );

    // Price vs SMA filter
    if (filters.priceAboveSma !== null) {
      filtered = filtered.filter(
        (item) => (item.price > item.sma50) === filters.priceAboveSma
      );
    }

    // My Strategy filter (Pro/Premium only — permission checked at render, but also guard here)
    if (myStrategyOnly) {
      filtered = filtered.filter((item) => item.strategyMatch === true);
    }

    // Sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'change':
          comparison = a.change - b.change;
          break;
        case 'rsi':
          comparison = a.rsi - b.rsi;
          break;
        case 'price':
          comparison = a.price - b.price;
          break;
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    setFilteredResults(filtered);
  };

  const toggleMarket = (market: string) => {
    setFilters((prev) => ({
      ...prev,
      markets: prev.markets.includes(market)
        ? prev.markets.filter((m) => m !== market)
        : [...prev.markets, market],
    }));
  };

  const toggleSector = (sector: string) => {
    setFilters((prev) => ({
      ...prev,
      sectors: prev.sectors.includes(sector)
        ? prev.sectors.filter((s) => s !== sector)
        : [...prev.sectors, sector],
    }));
  };

  const toggleSignalType = (signal: SignalType) => {
    if (!hasSignalDirection) return;
    setFilters((prev) => ({
      ...prev,
      signalTypes: prev.signalTypes.includes(signal)
        ? prev.signalTypes.filter((s) => s !== signal)
        : [...prev.signalTypes, signal],
    }));
  };

  const toggleTiming = (timing: 'now' | 'next_candle' | 'watch') => {
    setFilters((prev) => ({
      ...prev,
      timingFilter: prev.timingFilter.includes(timing)
        ? prev.timingFilter.filter((t) => t !== timing)
        : [...prev.timingFilter, timing],
    }));
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setSearchQuery('');
  };

  const toggleWatchlist = async (symbol: string) => {
    // Update local state immediately for UI responsiveness
    const isInWatchlist = watchlist.includes(symbol);
    setWatchlist((prev) => (isInWatchlist ? prev.filter((s) => s !== symbol) : [...prev, symbol]));

    try {
      // Get or create default watchlist
      let defaultWatchlist = watchlists.find(w => w.name === 'Main Watchlist') || watchlists[0];

      if (!defaultWatchlist) {
        // Create default watchlist if none exists
        defaultWatchlist = await api.createWatchlist('Main Watchlist');
        setWatchlists([defaultWatchlist]);
      }

      if (isInWatchlist) {
        // Remove from watchlist
        const item = defaultWatchlist.items.find(i => i.symbol === symbol);
        if (item) {
          await api.removeWatchlistItem(defaultWatchlist.id, item.id);
        }
      } else {
        // Add to watchlist
        const assetInfo = results.find(r => r.symbol === symbol);
        await api.addWatchlistItem(
          defaultWatchlist.id,
          symbol,
          assetInfo?.name || symbol.split('.')[0],
          assetInfo?.exchange || 'UNKNOWN'
        );
      }

      // Reload watchlists to sync with backend
      await loadWatchlists();
    } catch (error) {
      console.error('Error toggling watchlist:', error);
      // Revert local state on error
      setWatchlist((prev) => (isInWatchlist ? [...prev, symbol] : prev.filter((s) => s !== symbol)));
    }
  };

  const handleSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const exportToCSV = () => {
    const headers = hasSignalDirection
      ? ['Symbol', 'Name', 'Exchange', 'Sector', 'Price', 'Change %', 'RSI', 'Signal']
      : ['Symbol', 'Name', 'Exchange', 'Sector', 'Price', 'Change %', 'RSI'];
    const rows = filteredResults.map((item) => [
      item.symbol,
      item.name,
      item.exchange,
      item.sector,
      item.price.toFixed(2),
      item.change.toFixed(2),
      item.rsi.toFixed(1),
      ...(hasSignalDirection ? [item.signal] : []),
    ]);

    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `screener-results-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const activeFilterCount =
    (filters.markets.length < 3 ? 1 : 0) +
    (filters.sectors.length > 0 ? 1 : 0) +
    (hasSignalDirection && filters.signalTypes.length > 0 ? 1 : 0) +
    (filters.timingFilter.length > 0 ? 1 : 0) +
    (filters.rsiMin > 0 || filters.rsiMax < 100 ? 1 : 0) +
    (filters.changeMin > -100 || filters.changeMax < 100 ? 1 : 0) +
    (filters.priceAboveSma !== null ? 1 : 0);

  // Pagination calculations
  const totalPages = Math.ceil(filteredResults.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedResults = filteredResults.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="p-6">
        <LoadingSpinner size="lg" />
        <p className="text-center text-slate-400 mt-4">Loading market data...</p>
      </div>
    );
  }

  return (
    <PermissionGate permission="feature:screener-signals" fallback={<div className="p-8"><UpgradeNudge tier="pro" features={['AI signal screener', 'Buy/sell signals across all markets', 'Fundamental & technical filters', 'Export results to CSV']} /></div>}>
    <div className="p-4 lg:p-6 pb-20 lg:pb-0">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <SlidersHorizontal className="text-teal-400" />
            Asset Screener
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Find investment opportunities across JSE, US markets, and crypto
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
              showFilters ? 'bg-teal-500/20 text-teal-400' : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
            }`}
            title={showFilters ? 'Hide filters' : 'Show filters'}
          >
            <Filter size={16} />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            {activeFilterCount > 0 && (
              <span className="bg-teal-500 text-slate-900 px-1.5 py-0.5 rounded-full text-xs font-bold">
                {activeFilterCount}
              </span>
            )}
            {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <button
            onClick={loadScreenerResults}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors"
          >
            <RefreshCw size={16} />
            Refresh
          </button>

          {/* Watchlist Filter Dropdown */}
          <div className="relative">
            <select
              value={selectedWatchlistId || ''}
              onChange={(e) => setSelectedWatchlistId(e.target.value ? Number(e.target.value) : null)}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors appearance-none pr-8 cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All Stocks</option>
              {watchlists.map((watchlist) => (
                <option key={watchlist.id} value={watchlist.id}>
                  ⭐ {watchlist.name} ({watchlist.items.length})
                </option>
              ))}
            </select>
            <List size={16} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" />
          </div>

          {/* Save/Load Filter Buttons */}
          <div className="relative">
            <button
              onClick={() => setShowLoadMenu(!showLoadMenu)}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors"
              title="Load saved filter"
            >
              <FolderOpen size={16} />
              Load
              {savedFilters.length > 0 && (
                <span className="bg-teal-500 text-slate-900 px-1.5 py-0.5 rounded-full text-xs font-bold">
                  {savedFilters.length}
                </span>
              )}
            </button>

            {/* Load Menu Dropdown */}
            {showLoadMenu && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50">
                <div className="p-2 border-b border-slate-700">
                  <p className="text-xs text-slate-400 font-medium">Saved Filters</p>
                </div>
                {savedFilters.length === 0 ? (
                  <div className="p-4 text-center text-sm text-slate-500">
                    No saved filters yet
                  </div>
                ) : (
                  <div className="max-h-60 overflow-y-auto">
                    {savedFilters.map((sf, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between px-3 py-2 hover:bg-slate-700/50 cursor-pointer group"
                      >
                        <div
                          className="flex-1"
                          onClick={() => loadSavedFilter(sf)}
                        >
                          <p className="text-sm text-slate-200">{sf.name}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(sf.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSavedFilter(index);
                          }}
                          className="p-1 text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            onClick={() => setShowSaveDialog(true)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-500/20 text-teal-400 hover:bg-teal-500/30 rounded-lg text-sm transition-colors"
            title="Save current filter"
          >
            <Save size={16} />
            Save
          </button>
          {hasStrategyPermission && (
            <>
              <button
                onClick={() => setMyStrategyOnly((prev) => !prev)}
                aria-pressed={myStrategyOnly}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  myStrategyOnly
                    ? 'bg-teal-500 text-slate-900'
                    : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                }`}
                title="Show only signals matching your active strategy"
              >
                {myStrategyOnly ? '✓ ' : ''}My Strategy
              </button>
              <button
                onClick={runStrategyMatch}
                disabled={strategyMatchLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  strategyMatchError
                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                    : strategyMatchDone
                    ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                    : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                title="Run strategy match against your active strategies"
              >
                {strategyMatchLoading ? (
                  <><RefreshCw size={14} className="animate-spin" />Matching...</>
                ) : strategyMatchError ? (
                  'Retry Match'
                ) : strategyMatchDone ? (
                  <>✓ Strategy Matched{strategyMatchTimestamp ? ` (at ${strategyMatchTimestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})` : ''}</>
                ) : (
                  'Match Strategy'
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Save Filter Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-96 shadow-2xl">
            <h3 className="text-lg font-semibold mb-4">Save Current Filter</h3>
            <input
              type="text"
              value={newFilterName}
              onChange={(e) => setNewFilterName(e.target.value)}
              placeholder="Enter filter name..."
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg mb-4 focus:outline-none focus:border-teal-500"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && saveCurrentFilter()}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowSaveDialog(false);
                  setNewFilterName('');
                }}
                className="px-4 py-2 text-slate-400 hover:text-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveCurrentFilter}
                disabled={!newFilterName.trim()}
                className="px-4 py-2 bg-teal-500 text-slate-900 font-medium rounded-lg hover:bg-teal-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close load menu */}
      {showLoadMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowLoadMenu(false)}
        />
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-300">Filter Options</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={resetFilters}
                className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"
              >
                <X size={12} />
                Reset All
              </button>
              <button
                onClick={() => setShowFilters(false)}
                className="text-xs text-slate-400 hover:text-teal-400 flex items-center gap-1 transition-colors"
                title="Collapse filters"
              >
                <ChevronUp size={12} />
                Collapse
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Market Filter */}
            <div>
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2 block">
                Markets
              </label>
              <div className="flex flex-wrap gap-2">
                {['JSE', 'US', 'Crypto', 'Commodities'].map((market) => (
                  <button
                    key={market}
                    onClick={() => toggleMarket(market)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      filters.markets.includes(market)
                        ? 'bg-teal-500/20 text-teal-400 border border-teal-500/50'
                        : 'bg-slate-700 text-slate-400 border border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    {market}
                  </button>
                ))}
              </div>
            </div>

            {/* RSI Range */}
            <div>
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2 block">
                RSI Range
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={filters.rsiMin}
                  onChange={(e) => setFilters({ ...filters, rsiMin: Number(e.target.value) })}
                  className="w-16 px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-sm text-center"
                />
                <span className="text-slate-500">to</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={filters.rsiMax}
                  onChange={(e) => setFilters({ ...filters, rsiMax: Number(e.target.value) })}
                  className="w-16 px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-sm text-center"
                />
              </div>
              <div className="flex gap-1 mt-2">
                <button
                  onClick={() => setFilters({ ...filters, rsiMin: 0, rsiMax: 30 })}
                  className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded hover:bg-emerald-500/30"
                >
                  Oversold
                </button>
                <button
                  onClick={() => setFilters({ ...filters, rsiMin: 70, rsiMax: 100 })}
                  className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"
                >
                  Overbought
                </button>
              </div>
            </div>

            {/* Change Range */}
            <div>
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2 block">
                Daily Change %
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={filters.changeMin}
                  onChange={(e) => setFilters({ ...filters, changeMin: Number(e.target.value) })}
                  className="w-16 px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-sm text-center"
                />
                <span className="text-slate-500">to</span>
                <input
                  type="number"
                  value={filters.changeMax}
                  onChange={(e) => setFilters({ ...filters, changeMax: Number(e.target.value) })}
                  className="w-16 px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-sm text-center"
                />
              </div>
              <div className="flex gap-1 mt-2">
                <button
                  onClick={() => setFilters({ ...filters, changeMin: 0, changeMax: 100 })}
                  className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded hover:bg-emerald-500/30"
                >
                  Gainers
                </button>
                <button
                  onClick={() => setFilters({ ...filters, changeMin: -100, changeMax: 0 })}
                  className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"
                >
                  Losers
                </button>
              </div>
            </div>

            {/* Price vs SMA */}
            <div>
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2 block">
                Price vs SMA 50
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilters({ ...filters, priceAboveSma: filters.priceAboveSma === true ? null : true })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    filters.priceAboveSma === true
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
                      : 'bg-slate-700 text-slate-400 border border-slate-600 hover:border-slate-500'
                  }`}
                >
                  Above SMA
                </button>
                <button
                  onClick={() => setFilters({ ...filters, priceAboveSma: filters.priceAboveSma === false ? null : false })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    filters.priceAboveSma === false
                      ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                      : 'bg-slate-700 text-slate-400 border border-slate-600 hover:border-slate-500'
                  }`}
                >
                  Below SMA
                </button>
              </div>
            </div>
          </div>

          {/* Signal Type Filter (locked until CAT II compliance enabled) */}
          {hasSignalDirection && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2 block">
                Signal Type
              </label>
              <div className="flex flex-wrap gap-2">
                {signalOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => toggleSignalType(option.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      filters.signalTypes.includes(option.value)
                        ? option.value.includes('buy')
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
                          : option.value.includes('sell')
                          ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                          : 'bg-slate-500/20 text-slate-300 border border-slate-500/50'
                        : 'bg-slate-700 text-slate-400 border border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Timing Filter */}
          <div className="mt-4 pt-4 border-t border-slate-700">
            <label className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2 block">
              Timing
            </label>
            <div className="flex flex-wrap gap-2">
              {timingOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => toggleTiming(option.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    filters.timingFilter.includes(option.value)
                      ? option.value === 'now'
                        ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                        : option.value === 'next_candle'
                        ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
                        : 'bg-purple-500/20 text-purple-400 border border-purple-500/50'
                      : 'bg-slate-700 text-slate-400 border border-slate-600 hover:border-slate-500'
                  }`}
                >
                  {option.icon} {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Sector Filter */}
          <div className="mt-4 pt-4 border-t border-slate-700">
            <label className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2 block">
              Sectors
            </label>
            <div className="flex flex-wrap gap-2">
              {availableSectors.map((sector) => (
                <button
                  key={sector}
                  onClick={() => toggleSector(sector)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    filters.sectors.includes(sector)
                      ? 'bg-teal-500/20 text-teal-400 border border-teal-500/50'
                      : 'bg-slate-700 text-slate-400 border border-slate-600 hover:border-slate-500'
                  }`}
                >
                  {sector}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile search — visible only below lg breakpoint */}
      <div className="lg:hidden mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="Search symbols or names..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-teal-500"
          />
        </div>
        <p className="text-xs text-slate-500 mt-1">
          {filteredResults.length} asset{filteredResults.length !== 1 ? 's' : ''}
          {filteredResults.length < results.length && ` (filtered from ${results.length})`}
        </p>
      </div>

      {/* Mobile card list — visible only below lg breakpoint */}
      <div className="space-y-3 lg:hidden mb-6">
        {filteredResults.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">
            No results match your filters. Try adjusting your criteria.
          </p>
        ) : (
          paginatedResults.map((item) => (
            <MobileDataCard
              key={item.symbol}
              onClick={() => navigate(`/asset/${item.symbol}`)}
              fields={[
                {
                  label: 'Symbol',
                  value: <span className="font-mono font-bold text-teal-400">{item.symbol.split('.')[0]}</span>,
                },
                {
                  label: 'Name',
                  value: item.name,
                },
                {
                  label: 'Price',
                  value: <span className="font-mono">{item.currency === 'ZAR' ? 'R' : '$'}{item.price.toFixed(2)}</span>,
                },
                {
                  label: 'Change',
                  value: (
                    <span className={
                      item.change > 0 ? 'text-green-400' : item.change < 0 ? 'text-red-400' : 'text-gray-400'
                    }>
                      {item.change > 0 ? '+' : ''}{item.change.toFixed(2)}%
                    </span>
                  ),
                },
                {
                  label: 'Market Cap',
                  value: item.marketCap || '—',
                },
              ]}
            />
          ))
        )}
      </div>

      {/* Search and Results Header — desktop table */}
      <div className="hidden lg:block bg-slate-800 rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-700/50 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="Search symbols or names..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-sm w-64 focus:outline-none focus:border-teal-500"
              />
            </div>
            <span className="text-sm text-slate-400">
              Showing <span className="text-teal-400 font-semibold">{startIndex + 1}-{Math.min(endIndex, filteredResults.length)}</span> of {filteredResults.length} assets
              {filteredResults.length < results.length && <span className="text-slate-500"> (filtered from {results.length})</span>}
            </span>
            {!hasSignalDirection && (
              <span className="text-xs text-amber-300 block">
                Signal direction is hidden — complete FAIS CAT II compliance in Settings to reveal it.
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors"
            >
              <Download size={14} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Results Table */}
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <thead>
              <tr className="bg-slate-800/80 border-b border-slate-700/50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase w-10"></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase w-12"></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase w-32">Symbol</th>
                <th
                  className="pl-4 pr-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase cursor-pointer hover:text-slate-200 w-40"
                  onClick={() => handleSort('name')}
                >
                  Name {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-2 py-3 text-left text-xs font-semibold text-slate-400 uppercase w-24">Exchange</th>
                <th className="pl-2 pr-1 py-3 text-left text-xs font-semibold text-slate-400 uppercase w-28">Sector</th>
                <th
                  className="pl-3 pr-1 py-3 text-right text-xs font-semibold text-slate-400 uppercase cursor-pointer hover:text-slate-200 w-24"
                  onClick={() => handleSort('price')}
                >
                  Price {sortBy === 'price' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="pl-1 pr-2 py-3 text-right text-xs font-semibold text-slate-400 uppercase cursor-pointer hover:text-slate-200"
                  onClick={() => handleSort('change')}
                >
                  Change {sortBy === 'change' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="px-2 py-3 text-right text-xs font-semibold text-slate-400 uppercase cursor-pointer hover:text-slate-200"
                  onClick={() => handleSort('rsi')}
                >
                  RSI {sortBy === 'rsi' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                {hasSignalDirection && (
                  <th className="px-2 py-3 text-center text-xs font-semibold text-slate-400 uppercase">Signal</th>
                )}
                {hasStrategyPermission && (
                  <th className="px-2 py-3 text-center text-xs font-semibold text-slate-400 uppercase">Strategy</th>
                )}
                <th className="px-2 py-3 text-center text-xs font-semibold text-slate-400 uppercase">AI</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {filteredResults.length === 0 ? (
                <tr>
                  <td
                    colSpan={9 + (hasSignalDirection ? 1 : 0) + (hasStrategyPermission ? 1 : 0)}
                    className="px-4 py-8 text-center text-slate-400"
                  >
                    {myStrategyOnly && !strategyMatchDone
                      ? 'Run Match Strategy to filter by your strategies.'
                      : myStrategyOnly
                      ? 'No signals match your active strategy. Try disabling the My Strategy filter.'
                      : 'No results match your filters. Try adjusting your criteria.'}
                  </td>
                </tr>
              ) : (
                paginatedResults.map((item) => (
                  <ScreenerAssetRow
                    key={item.symbol}
                    symbol={item.symbol}
                    name={item.name}
                    price={item.price}
                    change={item.change}
                    previousClose={item.previousClose}
                    rsi={item.rsi}
                    signal={item.signal}
                    timing={item.timing}
                    exchange={item.exchange}
                    sector={item.sector}
                    currency={item.currency}
                    watchlist={watchlist}
                    onToggleWatchlist={toggleWatchlist}
                    aiRecommendation={item.aiRecommendation}
                    aiConfidence={item.aiConfidence}
                    aiLoading={aiLoadingSymbols.has(item.symbol)}
                    onRequestAI={() => requestAIAnalysis(item.symbol)}
                    dayDropOverride={item.dayDropOverride}
                    showStrategyColumn={hasStrategyPermission}
                    matchedStrategy={item.matchedStrategy}
                    strategyBreakdown={item.strategyBreakdown}
                    onRequestStrategy={() => requestStrategyBreakdown(item.symbol)}
                    strategyLoading={strategyLoadingSymbols.has(item.symbol)}
                    showSignalColumn={hasSignalDirection}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {filteredResults.length > 0 && totalPages > 1 && (
          <div className="px-4 py-3 border-t border-slate-700/50 flex items-center justify-between">
            <div className="text-sm text-slate-400">
              Page <span className="text-teal-400 font-semibold">{currentPage}</span> of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-700"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-700"
              >
                Previous
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? 'bg-teal-500 text-slate-900'
                          : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-700"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-700"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </PermissionGate>
  );
};

export default Screener;
